<template>
  <dx-modal title="Novo solicitante ou indicação"
            :value="show"
            @input="close"
            id="modal-referral">
    <div class="toast toast-warning mb-2" v-if="readonly">
      Este solicitante já está cadastrado. Os dados abaixo são apenas para consulta.
    </div>
    <div class="columns">
      <div class="column col-12 form-group"
           :class="{'has-error': $v.form.type.$error}">
        <label for="referral-type" class="form-label">Tipo</label>
        <select id="referral-type" class="form-select"
                v-model="form.type"
                :disabled="blocked"
                @change="onTypeChange"
                @blur="$v.form.type.$touch()">
          <option value="">[Selecione]</option>
          <option value="collaborator">Colaborador</option>
          <option value="patient">Paciente</option>
          <option value="partner">Clínica/Laboratório</option>
          <option value="professional">Profissional da saúde</option>
        </select>
        <template v-if="$v.form.type.$error">
          <div class="form-input-hint"
               v-if="!$v.form.type.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-4 col-sm-12 form-group"
           :class="{'has-error': $v.form.council.name.$error}"
           v-if="isProfessional">
        <label for="referral-council" class="form-label">Conselho</label>
        <select id="referral-council" class="form-select"
                v-model="form.council.name"
                :disabled="blocked"
                @blur="$v.form.council.name.$touch()">
          <option value="">[Selecione]</option>
          <option v-for="(text, value) in council.councils"
                  :value="value" :key="value">{{ value }}</option>
        </select>
        <template v-if="$v.form.council.name.$error">
          <div class="form-input-hint"
               v-if="!$v.form.council.name.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-4 col-sm-12 form-group"
           :class="{'has-error': $v.form.council.state.$error}"
           v-if="isProfessional">
        <label for="referral-state" class="form-label">UF</label>
        <select id="referral-state" class="form-select"
                v-model="form.council.state"
                :disabled="blocked"
                @blur="$v.form.council.state.$touch()">
          <option value="">[Selecione]</option>
          <option v-for="(text, value) in states"
                  :value="value" :key="value">{{ value }}</option>
        </select>
        <template v-if="$v.form.council.state.$error">
          <div class="form-input-hint"
               v-if="!$v.form.council.state.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-4 col-sm-12 form-group"
           :class="{'has-error': $v.form.council.record.$error}"
           v-if="isProfessional">
        <label for="referral-record" class="form-label">Número</label>
        <div class="input-group">
          <input type="text" id="referral-record" class="form-input"
                 placeholder="00000"
                 v-model="form.council.record"
                 :disabled="notCouncil || blocked"
                 @change="findByCouncil"
                 @blur="$v.form.council.record.$touch()">
          <button class="btn btn-action input-group-btn btn-icon"
                  :class="[readonly ? 'btn-gray' : 'btn-neutral', {loading: finding}]"
                  :disabled="notCouncil || finding"
                  @click="readonly ? reset() : findByCouncil()"
                  tabindex="-1">
            <fa-icon :icon="['fal', readonly ? 'times' : 'search']"/>
          </button>
        </div>
        <template v-if="$v.form.council.record.$error">
          <div class="form-input-hint"
               v-if="!$v.form.council.record.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-12 form-group"
           :class="{'has-error': $v.form.name.$error}">
        <label class="form-label">Nome Completo</label>
        <dx-autocomplete
          v-model="referral"
          :source="findReferral"
          label="name"
          track-by="id"
          :readonly="readonly"
          :disabled="notCouncil || finding"
          :debounce="800"
          @select="setReferral"
          @blur="onNameBlur"
          placeholder="Informe o nome conforme está no documento"
          input-id="referral-name">
          <button slot="action" @click="reset"
                  class="btn btn-action input-group-btn btn-icon"
                  :class="form.name ? 'btn-gray' : 'btn-neutral'"
                  :disabled="notCouncil || finding"
                  tabindex="-1">
            <fa-icon :icon="['fal', form.name ? 'times' : 'search']" />
          </button>
          <template v-slot="{ item }">
            <div>{{ item.name }}</div>
            <small class="text-primary" v-if="item.id === null">(novo cadastro)</small>
            <small class="text-gray" v-else>{{ itemDescription(item) }}</small>
          </template>
        </dx-autocomplete>
        <template v-if="$v.form.name.$error">
          <div class="form-input-hint"
               v-if="!$v.form.name.required">Campo obrigatório</div>
        </template>
      </div>
      <div class="column col-12 form-group"
           :class="{'has-error': $v.form.specialty.code.$error}"
           v-if="isProfessional">
        <label class="form-label">Especialidade</label>
        <dx-autocomplete
          v-model="specialty"
          :source="findSpecialty"
          label="name"
          track-by="id"
          :readonly="readonly || !!form.specialty.code"
          :disabled="notCouncil || finding"
          :debounce="800"
          @select="setSpecialty"
          @blur="$v.form.specialty.code.$touch()"
          placeholder="Informe o código ou nome..."
          input-id="referral-specialty">
          <button slot="action" @click="unsetSpecialty"
                  class="btn btn-action input-group-btn btn-icon"
                  :class="form.specialty.code ? 'btn-gray' : 'btn-neutral'"
                  :disabled="notCouncil || blocked"
                  tabindex="-1">
            <fa-icon :icon="['fal', form.specialty.code ? 'times' : 'search']" />
          </button>
          <template v-slot="{ item }">
            <a>
              <fa-icon :icon="['fal', 'file-medical-alt']" class="text-primary"/>
              <span class="ml-1">{{ item.code }} - {{ item.name }}</span>
            </a>
          </template>
        </dx-autocomplete>
        <template v-if="$v.form.specialty.code.$error">
          <div class="form-input-hint"
               v-if="!$v.form.specialty.code.required">Campo obrigatório</div>
        </template>
      </div>
    </div>
    <template slot="footer">
      <button class="btn btn-primary mr-1"
              v-if="!readonly"
              @click="save" :disabled="saving || finding"
              :class="{loading: saving}">
        Salvar
      </button>
      <button class="btn btn-primary mr-1" @click="reset" v-else>
        Novo cadastro
      </button>
      <button class="btn" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { required } from 'vuelidate/src/validators';
import formMixin from 'src/mixins/form';
import * as council from 'src/data/council-types';
import states from 'src/data/states';

export default {
  mixins: [formMixin],
  props: {
    show: {
      type: Boolean,
    },
  },
  data() {
    return {
      council,
      states,
      finding: false,
      saving: false,
      readonly: false,
      referral: null,
      specialty: null,
      form: this.blankForm(),
    };
  },
  validations() {
    const rules = {
      form: {
        name: { required },
        type: { required },
        council: {
          name: {},
          state: {},
          record: {},
        },
        specialty: {
          code: {},
        },
      },
    };

    if (this.isProfessional) {
      rules.form.council = {
        name: { required },
        state: { required },
        record: { required },
      };
      rules.form.specialty = {
        code: { required },
      };
    }

    return rules;
  },
  computed: {
    isProfessional() {
      return this.form.type === 'professional';
    },
    notCouncil() {
      return this.isProfessional
        && !(this.form.council.name && this.form.council.state);
    },
    blocked() {
      return this.readonly || this.finding;
    },
  },
  methods: {
    onTypeChange() {
      const { type } = this.form;
      this.reset();
      this.form.type = type;
    },
    reset() {
      this.form = this.blankForm();
      this.referral = null;
      this.specialty = null;
      this.readonly = false;
      this.$v.form.$reset();
    },
    findReferral(search) {
      return this.$http.get('/referrals', { params: { search } })
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [{ id: null, name: search }];
          }
          return data.items;
        });
    },
    setReferral(data) {
      if (!data) {
        return;
      }

      if (!data.id) {
        this.referral = { id: null, name: data.name };
        this.form.name = data.name;
        return;
      }

      this.setExisting(data);
    },
    setExisting(data) {
      this.referral = {
        id: data.id,
        name: data.name,
      };
      this.form.name = data.name;
      this.form.type = data.type;

      if (data.council && data.council.record) {
        this.form.council = {
          name: data.council.name,
          state: data.council.state,
          record: data.council.record,
        };
      }

      if (data.specialty && data.specialty.code) {
        this.setSpecialty(data.specialty);
      } else {
        this.unsetSpecialty();
      }

      this.readonly = true;
      this.$v.form.$reset();
      this.$toast.show('Registro já cadastrado', { type: 'warning' });
    },
    onNameBlur(text) {
      if (this.readonly) {
        return;
      }
      this.form.name = text;
      this.$v.form.name.$touch();
    },
    itemDescription(item) {
      if (item.council && item.council.record) {
        return `${item.council.name}-${item.council.state} ${item.council.record}`;
      }
      return this.getTypeName(item.type);
    },
    getTypeName(type) {
      if (type === 'professional') {
        return 'Profissional da saúde';
      }
      if (type === 'collaborator') {
        return 'Colaborador';
      }
      if (type === 'partner') {
        return 'Clínica/Laboratório';
      }
      return 'Paciente';
    },
    findSpecialty(search) {
      const params = {
        search,
        isSpecialty: true,
      };

      return this.$http.get('/services/cbos', { params })
        .then(({ data }) => data.items);
    },
    setSpecialty(specialty) {
      if (!specialty) {
        return;
      }
      this.specialty = {
        code: specialty.code,
        name: specialty.name,
      };
      this.form.specialty = {
        code: specialty.code,
        name: specialty.name,
      };
    },
    unsetSpecialty() {
      this.form.specialty = this.blankForm().specialty;
      this.specialty = null;
    },
    findByCouncil() {
      if (this.readonly
        || !this.form.council.name
        || !this.form.council.state
        || !this.form.council.record
      ) {
        return null;
      }

      this.finding = true;

      return this.findExisting()
        .then((found) => {
          if (found) {
            this.setExisting(found);
          }
        })
        .catch(() => {})
        .finally(() => {
          this.finding = false;
        });
    },
    findExisting() {
      const params = this.isProfessional
        ? {
          councilName: this.form.council.name,
          councilState: this.form.council.state,
          councilRecord: this.form.council.record,
        }
        : { search: this.form.name };

      return this.$http.get('/referrals', { params })
        .then(({ data }) => {
          if (this.isProfessional) {
            return data.items[0] || null;
          }
          const name = this.form.name.trim().toLowerCase();
          return data.items.find(item => item.name.toLowerCase() === name) || null;
        });
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) {
        return null;
      }

      this.saving = true;

      const found = await this.findExisting().catch(() => null);

      if (found) {
        this.setExisting(found);
        this.saving = false;
        return null;
      }

      const referral = this.clone(this.form);

      if (!this.isProfessional) {
        delete referral.council;
        delete referral.specialty;
      }

      return this.$http.post('/referrals', referral)
        .then(() => {
          this.$toast.show('Registro salvo');
          this.$emit('saved');
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false;
        });
    },
    close() {
      this.$emit('close');
    },
    blankForm() {
      return {
        name: '',
        type: 'professional',
        council: {
          name: 'CRM',
          state: 'PR',
          record: '',
        },
        specialty: {
          code: '',
          name: '',
        },
      };
    },
  },
};
</script>

<style lang="scss">
</style>
